import { Icon, Tooltip } from '@lightspeed/design-system-react'
import { createSelector } from '@reduxjs/toolkit'
import { TFunction, useT } from '@transifex/react'
import React, { FunctionComponent, useMemo } from 'react'
import {
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccount,
  isUnfulfilledLineItem,
} from '../../../../functions/sale'
import { hasServiceOrder } from '../../../../functions/service'
import { useAppSelector } from '../../../../state/hooks'
import {
  ReduxCurrentSale,
  selectCustomFields,
  selectLineItemFulfilmentType,
  selectSaleCustomer,
  selectSalePayments,
  selectSaleStatus,
  selectServiceFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../../../state/store'

const selectData = createSelector(
  [
    selectSaleStatus,
    selectSaleCustomer,
    selectSalePayments,
    selectServiceFields,
    selectCustomFields,
    (state: RootState) => state.currentSale.payAccountBalance,
  ],
  (
    status,
    customer,
    payments,
    serviceFields,
    customFields,
    payAccountBalance
  ) => ({
    status,
    customer,
    payments,
    serviceFields,
    customFields,
    payAccountBalance,
  })
)

const getLockedTooltipMessages = (t: TFunction) => ({
  SERVICE_ORDER: {
    title: t('Service sale'),
    message: t(
      'This item can’t be removed from the sale because it is included in the service.',
      { _context: 'Tooltip on locked line item when sale is a service' }
    ),
  },
  ONACCOUNT: {
    title: t('Sale is on account'),
    message: t(
      'This item can’t be removed from the sale because it’s on account.',
      { _context: 'Tooltip on locked line item when sale is on account' }
    ),
  },
  LAYBY: {
    title: t('Sale is on layaway'),
    message: t(
      'This item can’t be removed from the sale because it’s on layaway.',
      { _context: 'Tooltip on locked line item when sale is on layaway' }
    ),
  },
  UNFULFILLED: {
    title: t('Sale has items for fulfillment'),
    message: t(
      'This item can’t be removed from the sale because it requires fulfillment.',
      {
        _context: 'Tooltip on locked line item as it is a fulfillment item',
      }
    ),
  },
  TAKE_NOW: {
    title: t('Sale has items for fulfillment'),
    message: t(
      'This item can’t be removed from the sale because there are items in this sale for fulfillment.',
      {
        _context:
          'Tooltip on locked line item when sale has items for fulfillment',
      }
    ),
  },
})

const isSaleServiceOrder = (
  sale: Pick<ReduxCurrentSale, 'status' | 'serviceFields' | 'customFields'>
) => {
  if (!sale.serviceFields) {
    return false
  }

  return (
    hasServiceOrder(sale.serviceFields, sale.customFields) ||
    isLegacyServiceSale(sale.status)
  )
}

const useLockedTooltip = (
  lineItemID: string
): { title: string; message: string } | null => {
  const t = useT()
  const messages = useMemo(() => getLockedTooltipMessages(t), [t])
  const sale = useAppSelector(selectData)
  const lineItemFulfilmentType = useAppSelector(
    selectLineItemFulfilmentType(lineItemID)
  )

  if (isSaleServiceOrder(sale)) {
    return messages.SERVICE_ORDER
  }

  if (isOnAccount(sale.status)) {
    return messages.ONACCOUNT
  }

  if (isLayby(sale.status)) {
    return messages.LAYBY
  }

  if (isUnfulfilledLineItem({ fulfilmentType: lineItemFulfilmentType })) {
    return messages.UNFULFILLED
  }

  if (isMarkedAsUnfulfilled(sale.status)) {
    // Take now item, when continuing a sale with fulfillment line item
    return messages.TAKE_NOW
  }

  return null
}

interface Props {
  lineItemID: string
}

export const LineItemLockedIconLegacy: FunctionComponent<Props> = ({
  lineItemID,
}) => {
  const message = useLockedTooltip(lineItemID)
  if (!message) {
    return null
  }

  return (
    <Tooltip
      direction="center"
      className="wr-tooltip"
      icon="fa-info-circle"
      canBeShown={true}
      header={message.title}
      message={message.message}
    >
      <Icon
        icon="fa-lock"
        className="vd-pa4"
        data-testid="line-item-locked-icon"
      />
    </Tooltip>
  )
}
